import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { DateValueType } from "react-tailwindcss-datepicker";
import AttachTaskSection from "@/components/AttachTaskSection";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem/ListItem";
import Progress from "@/components/Progress";
import SideSheetModal from "@/components/SideSheetModal";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import TextField from "@/components/TextField";
import { INTERACTION_ANALYTICS_EVENTS, INTERACTION_CREATE_SOURCES } from "@/components/interactions/amplitude";
import { refetchPersonInteractionViews } from "@/components/interactions/refetchPersonInteractionViews";
import {
  INTERACTION_CATEGORIES_METADATA_QUERY,
  type InteractionCategoriesMetadataData
} from "@/components/interactions/queries";
import type { SummaryPayload } from "@/components/interactions/types";
import { AlgoliaSearchResults, getPersonDisplayName, type PersonHit } from "@/components/AlgoliaSearch";
import { renderCreateInteractionPersonHit } from "@/layouts/AppLayout/CreateGlobalInteraction/createInteractionFuzzyHitRenderer";
import { useCreateInteractionFuzzySearch } from "@/layouts/AppLayout/CreateGlobalInteraction/useCreateInteractionFuzzySearch";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useAmplitude from "@/hooks/useAmplitude";
import useAttachTask from "@/hooks/useAttachTask";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useFlags } from "@/hooks/useFeatureFlags";
import { useAiQuery } from "@/lib/aiHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate, type Path } from "@/router";
import {
  ACTIVE_CALL_SESSION_CHANGED_EVENT,
  clearActiveCallSelectionForCallSid,
  readActiveCallDraft,
  readActiveCallSelection,
  storeActiveCallDraft,
  storeActiveCallSelection,
  type ActiveCallDraft,
  type ActiveCallSelection
} from "./sessionStorage";
import { ACTIVE_CALL_FINALIZE_INTERACTION_MUTATION, ACTIVE_CALL_SELECTED_PERSON_QUERY } from "./queries";
import ProcessStepLineItem from "@/components/ProcessStepLineItem";
import useActiveCallTranscriptPoll from "./useActiveCallTranscriptPoll";
import {
  getCategorizerComments,
  getFinalizeDescription,
  resolveCallTranscriptSummaryStep,
  type TranscriptStatusStep
} from "./transcriptPollUtils";

type Props = {
  enabled: boolean;
};

const dateValueToISODate = (value: DateValueType): string | null => {
  if (!value || typeof value === "string" || !value.startDate) return null;

  const date = value.startDate instanceof Date ? value.startDate : new Date(value.startDate);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().split("T")[0];
};

const isoDateToDateValue = (value?: string | null): DateValueType => {
  if (!value) return null;

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  return { startDate: date, endDate: date };
};

const getDefaultTaskName = (personName?: string) => {
  return personName ? `Follow-up to call with ${personName}` : "Follow-up to active call";
};

const getTabLabel = (personName?: string) => {
  if (personName) return `Call Details: ${personName.split(" ")[0]}`;
  return "Call Details";
};

const FINALIZE_INTERACTION_ERROR_MESSAGE = "There was an error finalizing the interaction. Please try again later.";
const DEFAULT_CALL_INTERACTION_CATEGORY = "Member Assistance";

export default function ActiveCallSideSheet({ enabled }: Props) {
  const location = useLocation();
  const currentUser = useCurrentBackofficeUser();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const flags = useFlags();
  const transcriptSummaryEnabled = !!flags["flex-call-transcript-summary"];
  const canManageTasks = useCanManageTasksBool();
  const showAttachTask = canManageTasks;
  const taskHook = useAttachTask(currentUser);
  const {
    attachTask,
    clearAssignee,
    clearError,
    reset: resetTask,
    selectAssignee,
    setTaskAssigneeSearch,
    setTaskDueDate,
    setTaskIsPrivate,
    setTaskName,
    taskAssignee,
    taskAssigneeSearch,
    taskDueDate,
    taskErrors,
    taskIsPrivate,
    taskName,
    toggleAttachTask,
    usersData,
    usersLoading
  } = taskHook;
  const [createInteraction, { loading: creatingInteraction }] = useMutation(ACTIVE_CALL_FINALIZE_INTERACTION_MUTATION);
  const { fetchData: generateCategory, loading: generatingAiCategory } = useAiQuery<SummaryPayload>(
    "career-consultation-categorizer"
  );
  const [selection, setSelection] = useState<ActiveCallSelection | null>(() =>
    enabled ? readActiveCallSelection() : null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [finalizeError, setFinalizeError] = useState<string>();
  const [isChangingPerson, setIsChangingPerson] = useState(false);
  const hydratedCallSidRef = useRef<string>();
  const notesInputRef = useRef<HTMLInputElement>(null);
  const shouldFocusNotesAfterSelectionRef = useRef(false);
  const shouldFocusSearchAfterClearRef = useRef(false);
  const { data: selectedPersonData } = useQuery(ACTIVE_CALL_SELECTED_PERSON_QUERY, {
    variables: { pgaId: selection?.pgaId || "" },
    skip: !selection?.pgaId
  });
  const selectedPerson = selectedPersonData?.person;
  const selectedPersonName = selectedPerson?.displayName;
  const hasSelectedPerson = !!selection?.pgaId;
  const isCallEnded = !!selection?.ended;
  const {
    callSid: polledCallSid,
    transcriptSummary,
    transcriptSummaryStatus,
    interactionId,
    loading: transcriptStatusLoading,
    isPolling: transcriptStatusPolling
  } = useActiveCallTranscriptPoll({
    enabled: enabled && transcriptSummaryEnabled,
    callEnded: isCallEnded,
    callSid: selection?.callSid
  });
  const showPersonSearch = isChangingPerson || !selection?.pgaId;
  const { data: interactionCategoriesData } = useQuery<InteractionCategoriesMetadataData>(
    INTERACTION_CATEGORIES_METADATA_QUERY,
    {
      fetchPolicy: "cache-first",
      skip: !enabled
    }
  );
  const finalizingInteraction = creatingInteraction || generatingAiCategory;
  const validCategoryNames = useMemo(
    () => interactionCategoriesData?.interactionCategories.map((category) => category.name) ?? [],
    [interactionCategoriesData?.interactionCategories]
  );
  const transcriptSummaryStep =
    isCallEnded && transcriptSummaryEnabled
      ? resolveCallTranscriptSummaryStep(transcriptSummaryStatus, transcriptStatusLoading || transcriptStatusPolling)
      : null;
  const inCallTranscriptRecordingStep: TranscriptStatusStep | null =
    !isCallEnded && transcriptSummaryEnabled
      ? { status: "processing", headline: "Recording call transcript..." }
      : null;
  const transcriptStatusLineItem = inCallTranscriptRecordingStep ?? transcriptSummaryStep;

  const {
    query,
    displayValue,
    results,
    isLoading,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef,
    failed: personSearchFailed
  } = useCreateInteractionFuzzySearch({
    entity: "people",
    hitsPerPage: 10,
    skipListQuery: !enabled,
    getDisplayValue: getPersonDisplayName,
    onSelect: (hit: PersonHit) => {
      if (!selection) return;

      const pgaId = hit.pga_id || hit.legacy_id;
      const nextSelection: ActiveCallSelection = {
        callSid: selection.callSid,
        personId: hit.objectID,
        pgaId,
        ...(selection.ended ? { ended: true } : {})
      };

      storeActiveCallSelection(nextSelection);
      setSelection(nextSelection);
      setIsChangingPerson(false);
      setIsOpen(true);
      shouldFocusNotesAfterSelectionRef.current = true;
    }
  });

  useEffect(() => {
    if (!enabled) {
      setSelection(null);
      setIsOpen(false);
      return;
    }

    const refreshSelection = () => {
      const nextSelection = readActiveCallSelection();
      setSelection(nextSelection);
      if (!nextSelection) setIsOpen(false);
      if (nextSelection?.ended) setIsOpen(true);
    };

    refreshSelection();
    window.addEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
    window.addEventListener("storage", refreshSelection);

    return () => {
      window.removeEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
      window.removeEventListener("storage", refreshSelection);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !selection) return;

    const requestedCallSid = (location.state as { openActiveCallPanel?: string } | null)?.openActiveCallPanel;
    if (!requestedCallSid || requestedCallSid !== selection.callSid || selection.ended) return;

    setIsOpen(true);
    shouldFocusNotesAfterSelectionRef.current = true;
    navigate(`${location.pathname}${location.search}${location.hash}` as Path, {
      replace: true,
      state: null
    });
  }, [enabled, location, navigate, selection]);

  useEffect(() => {
    if (!selection?.callSid || !interactionId) return;
    if (polledCallSid !== selection.callSid) return;

    clearActiveCallSelectionForCallSid(selection.callSid);
  }, [interactionId, polledCallSid, selection?.callSid]);

  useEffect(() => {
    if (!selection) {
      hydratedCallSidRef.current = undefined;
      setNotes("");
      resetTask();
      return;
    }

    if (hydratedCallSidRef.current === selection.callSid) return;

    const draft = readActiveCallDraft();
    const matchingDraft = draft?.callSid === selection.callSid ? draft : null;

    hydratedCallSidRef.current = selection.callSid;
    setNotes(matchingDraft?.notes || "");

    if (matchingDraft?.attachTask) {
      toggleAttachTask(true, matchingDraft.taskName || getDefaultTaskName(selectedPersonName));
      setTaskName(matchingDraft.taskName || getDefaultTaskName(selectedPersonName));
      setTaskDueDate(isoDateToDateValue(matchingDraft.taskDueDate));
      setTaskIsPrivate(!!matchingDraft.taskIsPrivate);
      if (matchingDraft.taskAssignee === null) {
        clearAssignee();
      } else if (matchingDraft.taskAssignee) {
        selectAssignee(matchingDraft.taskAssignee);
      }
    } else {
      resetTask();
    }
  }, [
    clearAssignee,
    resetTask,
    selectAssignee,
    selection,
    selectedPersonName,
    setTaskDueDate,
    setTaskIsPrivate,
    setTaskName,
    toggleAttachTask
  ]);

  useEffect(() => {
    setIsChangingPerson(false);
  }, [selection?.callSid, selection?.pgaId]);

  useEffect(() => {
    if (!selection || hydratedCallSidRef.current !== selection.callSid) return;

    const draft: ActiveCallDraft = {
      callSid: selection.callSid,
      notes,
      attachTask: showAttachTask ? attachTask : false,
      taskName: showAttachTask ? taskName : undefined,
      taskAssignee: showAttachTask && attachTask ? taskAssignee : undefined,
      taskDueDate: showAttachTask ? dateValueToISODate(taskDueDate) : undefined,
      taskIsPrivate: showAttachTask ? taskIsPrivate : undefined
    };

    storeActiveCallDraft(draft);
  }, [attachTask, notes, selection, showAttachTask, taskAssignee, taskDueDate, taskIsPrivate, taskName]);

  useEffect(() => {
    if (!shouldFocusNotesAfterSelectionRef.current || !isOpen || !hasSelectedPerson || showPersonSearch) return;

    const timeoutId = window.setTimeout(() => {
      notesInputRef.current?.focus();
      shouldFocusNotesAfterSelectionRef.current = false;
    });

    return () => window.clearTimeout(timeoutId);
  }, [hasSelectedPerson, isOpen, selection?.pgaId, showPersonSearch]);

  useEffect(() => {
    if (!shouldFocusSearchAfterClearRef.current || !isOpen || hasSelectedPerson || !showPersonSearch) return;

    focusInput();
    shouldFocusSearchAfterClearRef.current = false;
  }, [focusInput, hasSelectedPerson, isOpen, showPersonSearch]);

  const selectedPersonSupportingText = useMemo(() => {
    if (!selection?.pgaId) return undefined;
    return `PGA ID: ${selection.pgaId}`;
  }, [selection?.pgaId]);

  if (!enabled || !selection) return null;

  const defaultTaskName = getDefaultTaskName(selectedPersonName);
  const personSearchTrailingIcon =
    displayValue || query ? (
      <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const clearSelectedPerson = () => {
    const nextSelection: ActiveCallSelection = {
      callSid: selection.callSid,
      ...(selection.ended ? { ended: true } : {})
    };
    handleClear();
    storeActiveCallSelection(nextSelection);
    setSelection(nextSelection);
    setIsChangingPerson(true);
    shouldFocusSearchAfterClearRef.current = true;
  };

  const clearCurrentCallSession = () => {
    clearActiveCallSelectionForCallSid(selection.callSid);
    resetTask();
    setNotes("");
    setFinalizeError(undefined);
    setIsChangingPerson(false);
  };

  const handleSidebarOpenChange = (open: boolean) => {
    if (isCallEnded && !open) return;
    setIsOpen(open);
  };

  const getFallbackCategories = (): string[] => {
    if (validCategoryNames.includes(DEFAULT_CALL_INTERACTION_CATEGORY)) {
      return [DEFAULT_CALL_INTERACTION_CATEGORY];
    }

    return validCategoryNames[0] ? [validCategoryNames[0]] : [DEFAULT_CALL_INTERACTION_CATEGORY];
  };

  const buildGeneratedCategories = async (): Promise<string[]> => {
    if (validCategoryNames.length === 0) {
      return getFallbackCategories();
    }

    const comments = transcriptSummaryEnabled ? getCategorizerComments(notes, transcriptSummary) : notes.trim() || null;
    if (!comments) {
      return getFallbackCategories();
    }

    try {
      const category = (
        await generateCategory(
          {
            mode: "Phone Call",
            subject: null,
            comments
          },
          { ignoreCache: true }
        )
      ).trim();

      if (validCategoryNames.includes(category)) return [category];
    } catch {
      // Fall through to Member Assistance so missing AI or topics cannot block wrap-up.
    }

    return getFallbackCategories();
  };

  const finalizeInteraction = async () => {
    if (!hasSelectedPerson) {
      return;
    }

    if (attachTask && !taskHook.validate()) return;

    if (!transcriptSummaryEnabled && !notes.trim()) {
      setFinalizeError("Call details are required.");
      notesInputRef.current?.focus();
      return;
    }

    const participantId = selectedPerson?.id || selection.personId;
    if (!participantId) {
      setFinalizeError(
        "We couldn't find the selected person. Discard these call details or select a different person."
      );
      return;
    }

    setFinalizeError(undefined);
    const taskInput = showAttachTask ? taskHook.getTaskInput(defaultTaskName) : null;
    const categories = await buildGeneratedCategories();

    try {
      const { data } = await createInteraction({
        variables: {
          input: {
            participantId,
            interactionDate: new Date().toISOString().split("T")[0],
            mode: "Phone Call",
            twilioFlexCallSid: selection.callSid,
            categories,
            description: transcriptSummaryEnabled ? (getFinalizeDescription(notes) ?? "") : notes.trim() || null,
            summary: null,
            privacyRestricted: false,
            ...(taskInput ? { task: taskInput } : {})
          }
        }
      });

      if (data?.createInteraction?.success) {
        const interactionId = data.createInteraction.interaction?.id;
        const taskId = data.createInteraction.task?.id;

        showSnackbar("Interaction finalized", {
          ...(interactionId &&
            selection.pgaId && {
              actions: [
                <Button
                  key="view-interaction"
                  variant="text"
                  onClick={() =>
                    navigate(`/people/${selection.pgaId}/interactions/interactions/${interactionId}` as Path)
                  }
                >
                  View Interaction
                </Button>
              ]
            })
        });
        if (taskInput && taskId) {
          showSnackbar("Task created", {
            actions: [
              <Button key="view-task" variant="text" onClick={() => navigate(`/tasks/${taskId}` as Path)}>
                View task
              </Button>
            ]
          });
        }
        trackEvent(INTERACTION_ANALYTICS_EVENTS.created, {
          source: INTERACTION_CREATE_SOURCES.incomingCall,
          audience: "single",
          mode: "Phone Call",
          activityType: categories,
          privacyRestricted: false,
          participants: [participantId],
          interactionId,
          pgaId: selection.pgaId,
          success: true
        });
        refetchPersonInteractionViews(apolloClient);
        clearCurrentCallSession();
      } else {
        setFinalizeError(data?.createInteraction?.message || FINALIZE_INTERACTION_ERROR_MESSAGE);
      }
    } catch {
      setFinalizeError(FINALIZE_INTERACTION_ERROR_MESSAGE);
    }
  };

  return (
    <SideSheetModal
      isSidebarOpen={isOpen}
      setSidebarOpen={handleSidebarOpenChange}
      title={isCallEnded ? "Finalize Call Details" : "Call Details"}
      persistentTabLabel={getTabLabel(selectedPersonName)}
      persistentTabStatus={isCallEnded ? "inactive" : "active"}
      disableClose={isCallEnded}
      disableFocusManagement={!showPersonSearch}
      actions={
        isCallEnded ? (
          <div className="flex justify-end space-x-4">
            <Button
              variant="filled"
              icon={finalizingInteraction ? <Progress size={20} color="currentColor" /> : undefined}
              onClick={finalizeInteraction}
              disabled={finalizingInteraction || !hasSelectedPerson}
            >
              {finalizingInteraction ? "Finalizing..." : "Finalize Interaction"}
            </Button>
            <Button
              variant="text"
              theme="error"
              icon="delete"
              onClick={clearCurrentCallSession}
              disabled={finalizingInteraction}
            >
              Discard
            </Button>
          </div>
        ) : (
          <Button variant="filled" onClick={() => setIsOpen(false)}>
            Save
          </Button>
        )
      }
    >
      <div className={`flex flex-col gap-6 ${showPersonSearch && !hasSelectedPerson ? "" : "pt-4"}`}>
        {finalizeError && (
          <Banner variant="error" description={finalizeError} inline closable={false} hideSecondaryDismiss />
        )}

        <section>
          {hasSelectedPerson && (
            <SubSectionHeading className="mb-3">
              {isCallEnded ? "Call Completed With" : "In Conversation With"}
            </SubSectionHeading>
          )}

          {!showPersonSearch && hasSelectedPerson && (
            <div className="rounded-lg border border-sys-brand-outline p-3">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <ListItem
                    compact
                    useAvatar
                    picture={selectedPerson?.profilePhoto || undefined}
                    headline={selectedPerson?.displayName || "Selected person"}
                    supportingText={selectedPersonSupportingText}
                  />
                </div>
                <button
                  type="button"
                  className="text-sys-brand-on-surface-variant"
                  aria-label="Clear selected person"
                  onClick={clearSelectedPerson}
                >
                  <Icon name="cancel" />
                </button>
              </div>
            </div>
          )}

          {showPersonSearch && (
            <div className="space-y-4">
              <div
                className={`sticky top-0 z-10 bg-sys-brand-surface-container-low ${isCallEnded ? "pt-6" : "pt-4"}`}
                data-algolia-sticky-search-header="true"
                ref={searchBoxRef}
              >
                <TextField
                  label={isCallEnded ? "Call Completed With" : "In Conversation With"}
                  placeholder="Search for a person"
                  value={displayValue || query}
                  onChangeText={(value) => {
                    setDisplayValue(undefined);
                    handleQueryChange(value);
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  leadingIcon="search"
                  trailingIcon={personSearchTrailingIcon}
                  variant="outlined"
                  className="w-full"
                />
              </div>
              {query.trim() && (
                <AlgoliaSearchResults
                  results={results}
                  query={query}
                  isLoading={isLoading}
                  highlightedIndex={highlightedIndex}
                  setHighlightedIndex={setHighlightedIndex}
                  handleItemSelected={handleItemSelected}
                  itemRefs={itemRefs}
                  resultsContainerRef={resultsContainerRef}
                  renderHit={renderCreateInteractionPersonHit}
                  noResultsMessage={personSearchFailed ? "Couldn't load people" : "No results found"}
                />
              )}
            </div>
          )}
        </section>

        {transcriptStatusLineItem && (
          <ProcessStepLineItem
            bordered
            variant="subtle"
            testId={isCallEnded ? "call-transcript-summary-status" : "call-transcript-recording-status"}
            status={transcriptStatusLineItem.status}
            headline={transcriptStatusLineItem.headline}
            supportingText={transcriptStatusLineItem.supportingText}
          />
        )}

        {hasSelectedPerson && (
          <>
            <TextField
              ref={notesInputRef}
              label={transcriptSummaryEnabled ? "Call Note" : "Call Details"}
              value={notes}
              onChangeText={(value) => {
                setNotes(value);
                setFinalizeError(undefined);
              }}
              type="textarea"
              rows={8}
              required={!transcriptSummaryEnabled && isCallEnded}
              error={!transcriptSummaryEnabled && isCallEnded && finalizeError === "Call details are required."}
              errorText={
                !transcriptSummaryEnabled && isCallEnded && finalizeError === "Call details are required."
                  ? finalizeError
                  : undefined
              }
              variant="outlined"
              className="w-full"
            />

            {showAttachTask && (
              <AttachTaskSection
                attachTask={attachTask}
                onToggle={(enabled) => toggleAttachTask(enabled, defaultTaskName)}
                taskName={taskName}
                onTaskNameChange={setTaskName}
                taskAssignee={taskAssignee}
                assigneeSearchQuery={taskAssigneeSearch}
                onAssigneeSearchChange={setTaskAssigneeSearch}
                selectAssignee={selectAssignee}
                clearAssignee={clearAssignee}
                assigneeResults={usersData}
                assigneeLoading={usersLoading}
                taskDueDate={taskDueDate}
                onDueDateChange={setTaskDueDate}
                taskIsPrivate={taskIsPrivate}
                onTaskIsPrivateChange={setTaskIsPrivate}
                errors={taskErrors}
                clearError={clearError}
                objectLabel="interaction"
                selectedTeamTags={taskHook.selectedTeamTags}
                teamSearchTerm={taskHook.teamSearchTerm}
                onTeamSearchChange={taskHook.setTeamSearchTerm}
                availableDepartmentOptions={taskHook.availableDepartmentOptions}
                onTeamVisibilitySelect={taskHook.handleTeamVisibilitySelect}
                onRemoveTeamTag={taskHook.removeTeamTag}
              />
            )}
          </>
        )}
      </div>
    </SideSheetModal>
  );
}
